import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  alternateLanguages?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title = 'My EZTopup - Mobile Top Up, Data Packages & E-Gift Cards in Myanmar',
  description = 'Easy and secure mobile top-up, data packages, and e-gift cards in Myanmar. Fast digital recharge for all operators with instant delivery.',
  keywords = 'mobile topup, data packages, e-gift cards, Myanmar, digital recharge, phone credit, instant topup, MPT, Ooredoo, Telenor, Atom, MyTel',
  image = '/logo.svg',
  url = 'https://myeztopup.com/',
  type = 'website',
  noIndex = false,
  alternateLanguages = true,
}) => {
  const { i18n } = useTranslation();
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Helper function to generate language-specific URL
    const generateLanguageUrl = (baseUrl: string, language: string) => {
      const urlObj = new URL(baseUrl);

      // Ensure trailing slash for consistency
      if (!urlObj.pathname.endsWith('/') && urlObj.pathname !== '/') {
        urlObj.pathname += '/';
      }

      if (language === 'mm') {
        urlObj.searchParams.set('lang', 'my'); // Use 'my' in URL for Myanmar
      } else if (language === 'en') {
        urlObj.searchParams.delete('lang'); // Default language, no param needed
      }
      return urlObj.toString();
    };

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    // Set canonical URL based on current language
    const canonicalUrl = generateLanguageUrl(url, i18n.language);
    canonical.setAttribute('href', canonicalUrl);

    // Add alternate language links (hreflang)
    if (alternateLanguages) {
      // Remove existing hreflang links
      const existingHreflangs = document.querySelectorAll('link[hreflang]');
      existingHreflangs.forEach((link) => link.remove());

      // Add hreflang for English (default)
      const hreflangEn = document.createElement('link');
      hreflangEn.setAttribute('rel', 'alternate');
      hreflangEn.setAttribute('hreflang', 'en');
      hreflangEn.setAttribute('href', generateLanguageUrl(url, 'en'));
      document.head.appendChild(hreflangEn);

      // Add hreflang for Myanmar
      const hreflangMy = document.createElement('link');
      hreflangMy.setAttribute('rel', 'alternate');
      hreflangMy.setAttribute('hreflang', 'my');
      hreflangMy.setAttribute('href', generateLanguageUrl(url, 'mm'));
      document.head.appendChild(hreflangMy);

      // Add x-default hreflang
      const hreflangDefault = document.createElement('link');
      hreflangDefault.setAttribute('rel', 'alternate');
      hreflangDefault.setAttribute('hreflang', 'x-default');
      hreflangDefault.setAttribute('href', generateLanguageUrl(url, 'en'));
      document.head.appendChild(hreflangDefault);
    }
  }, [title, description, keywords, image, url, type, noIndex, alternateLanguages, i18n.language]);

  return null;
};

export default SEO;
